import { Component, OnDestroy, Input } from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ProductA2c, PRODUCT_TYPE_F, JOIN_STRING_FOR_CONCAT } from '../../../../models/eorder';
import { EorderWizardService } from '../../../../services/eorder-wizard.service';
import { DialogVariablesComponent } from '../dialog-variables/dialog-variables.component';
import { InputNumberDirective } from '../../../../../shared/directives/input-number.directive';
import {
	NAMESPACE_RTPC_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../../shared/services/product-characteristics-translation.services';

interface AddToCartProvider {
	onClickAddToCart?: (templateNumber?: string, itemCount?: number) => void;
}

@Component({
	imports: [

		MatTooltipModule,

		MatInputModule,
		TranslateModule,
		NgIf,
		NgClass,
		MatIconModule,
		FormsModule,
		ReactiveFormsModule,
		InputNumberDirective
	],
	selector: 'copo-product-list-group-renderer',
	templateUrl: './product-list-header.component.html',
	styleUrl: './product-list-header.component.css'
})
export class ProductListHeaderComponent implements ICellRendererAngularComp, OnDestroy {
	@Input() addToCartHandler!: (templateNumber?: string, itemCount?: number) => void;
	params!: ICellRendererParams<unknown, unknown>;
	expanded = false;
	numberControl = new FormControl('1');
	isGroupHeader = false;
	isEmptyRow = false;
	groupHeaderName = '';
	isFC = false;
	templateNumber: number | undefined;
	variableList: string[] = [];
	confirmationPopupSub: Subscription | undefined;

	constructor(

		private readonly dialog: MatDialog,
		private readonly appWizardService: EorderWizardService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService
	) {}

	agInit(params: ICellRendererParams): void {
		if (!params.colDef) {
			return;
		}
		this.params = params;
		this.addToCartHandler = (<AddToCartProvider> params.colDef.cellRendererParams)
			.onClickAddToCart ?? (() => { /* no-op */ });
		this.isGroupHeader = !this.params.data;
		if (this.params.data) {
			this.isEmptyRow = Boolean((<ProductA2c> params.data).emptyRow);
		} else {
			this.isGroupHeader = true;
		}
		if (params.node.key) {
			const arr = params.node.key.split(JOIN_STRING_FOR_CONCAT);
			if (arr.length > 1 && arr[1]) {
				if (arr[1].trim() === PRODUCT_TYPE_F) {
					this.isFC = true;
				} else {
					this.templateNumber = Number(arr[1].trim());
					this.variableList = this.getVariables();
				}
			}
			this.groupHeaderName = arr[0] ? arr[0].trim() : '';
		}
		this.expandPanel();
	}

	ngOnDestroy(): void {
		if (this.confirmationPopupSub) {
			this.confirmationPopupSub.unsubscribe();
		}
	}

	openPopup(event: MouseEvent) {
		const variableElement = <HTMLElement> event.target;
		const rect = variableElement.getBoundingClientRect();

		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		let popupLeft = rect.left;
		let popupBottom = rect.bottom;

		if (popupLeft + 200 > viewportWidth) {
			popupLeft = viewportWidth - 300 - 10;
		}

		const popupHeight = this.getPopupHeightByVariableCount(this.variableList.length + 1);

		if (popupBottom + popupHeight > viewportHeight) {
			popupBottom = rect.top - popupHeight - 20;
		}

		this.dialog.open(DialogVariablesComponent, {
			panelClass: 'variables-dialog-class',
			data: {
				variables: this.variableList,
				bottom: popupBottom,
				left: popupLeft
			},
			backdropClass: 'custom-backdrop-class'
		});
	}

	expandPanel() {
		this.expanded = !this.expanded;
		this.params.node.setExpanded(this.expanded);
	}

	refresh(_params: ICellRendererParams) {
		return false;
	}

	addToCart() {
		this.addToCartHandler(String(this.templateNumber), Number(this.numberControl.value));
	}

	private getPopupHeightByVariableCount(variableCount: number) {
		// 16 approximate height of each variable
		// 30 top and bottom space of popup
		return (variableCount * 16) + 30;
	}

	private getVariables() {
		const variablesArr: string[] = [];
		if (this.templateNumber) {
			const templateAndDetail = this.appWizardService.getState().ui.getTemplateNumberDetails(
				this.templateNumber
			);
			if (templateAndDetail?.listOfVariables) {
				for (const variable of templateAndDetail.listOfVariables) {
					variablesArr.push(this.productCharacteristicsTranslationService.translate(
						NAMESPACE_RTPC_FIELD + variable
					));
				}
			}
		}
		return variablesArr.sort((a, b) => a.localeCompare(b));
	}
}
